import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, 
  MessageCircle, 
  Calendar, 
  Clock, 
  Smartphone, 
  CheckCircle, 
  Star, 
  BrainCircuit, 
  BarChart, 
  Users,
  Mail,
  PanelRight,
  Building,
  Layers,
  MessagesSquare,
  Zap,
  ChevronRight,
  Globe,
  ArrowRight,
  Rocket,
  Sparkles,
  Timer,
  BarChart2,
  TrendingDown,
  AlertCircle,
  LineChart,
  DollarSign,
  Mic,
  Volume2,
  Headphones,
  Phone
} from 'lucide-react';
import SectionTransitionEffect from '../components/ui/SectionTransitionEffect';
import { Link } from 'react-router-dom';
import SiteMetadata from '../components/SEO/SiteMetadata';

const Chip: React.FC = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }
    }
  };

  // Conversion rates by response time
  const conversionData = [
    { time: "Under 5 min", rate: 85, color: "bg-green-500" },
    { time: "5-10 min", rate: 65, color: "bg-lime-500" },
    { time: "10-30 min", rate: 45, color: "bg-yellow-500" },
    { time: "30-60 min", rate: 28, color: "bg-orange-500" },
    { time: "1-24 hours", rate: 12, color: "bg-red-500" },
    { time: "24+ hours", rate: 4, color: "bg-red-700" }
  ];

  // Business pain points
  const painPoints = [
    {
      icon: <Clock className="text-accent" size={24} />,
      title: "Missed After-Hours Leads",
      description: "Over 60% of leads come in after hours or on weekends, when your shop is closed and leads go cold."
    },
    {
      icon: <TrendingDown className="text-accent" size={24} />,
      title: "Declining Conversion Rates",
      description: "For every 5 minutes of delay in response time, your chance of qualifying a lead drops by 10%."
    },
    {
      icon: <DollarSign className="text-accent" size={24} />,
      title: "Revenue Leakage",
      description: "The average detailing shop loses $3,000-$5,000 in monthly revenue due to slow response times."
    },
    {
      icon: <AlertCircle className="text-accent" size={24} />,
      title: "Staff Burnout",
      description: "Your staff is overwhelmed with repetitive inquiries that could be handled automatically."
    }
  ];

  // CHiP features
  const features = [
    {
      icon: <Calendar className="text-accent" size={24} />,
      title: "24/7 Appointment Booking",
      description: "CHiP books appointments automatically based on your availability, handles reschedules, and sends reminders to reduce no-shows."
    },
    {
      icon: <Smartphone className="text-accent" size={24} />,
      title: "Multi-Channel Support",
      description: "Works seamlessly across SMS, website chat, Facebook Messenger, Instagram, WhatsApp, and email."
    },
    {
      icon: <BrainCircuit className="text-accent" size={24} />,
      title: "Industry-Specific Knowledge",
      description: "Pre-trained with deep knowledge of detailing services, paint protection, ceramic coatings, and common customer questions."
    },
    {
      icon: <MessagesSquare className="text-accent" size={24} />,
      title: "Natural Conversations",
      description: "Engages in human-like conversations that build rapport and trust with potential customers."
    },
    {
      icon: <Building className="text-accent" size={24} />,
      title: "CRM Integration",
      description: "Syncs with your existing systems to update customer records, appointments, and follow-ups automatically."
    },
    {
      icon: <Zap className="text-accent" size={24} />,
      title: "Lead Qualification",
      description: "Asks the right questions to qualify leads and prioritize high-value opportunities for your sales team."
    },
    {
      icon: <Users className="text-accent" size={24} />,
      title: "Smart Handoff",
      description: "Knows when to transfer complex conversations to your team, with full context provided."
    },
    {
      icon: <Layers className="text-accent" size={24} />,
      title: "Custom Response Flows",
      description: "Easily configure conversation paths for different services, promotions, and customer scenarios."
    }
  ];

  // ROI metrics
  const roiMetrics = [
    {
      metric: "24/7",
      description: "Coverage without staffing costs",
      icon: <Clock size={28} />
    },
    {
      metric: "3-5 min",
      description: "Average response time vs 5 hours",
      icon: <Zap size={28} />
    },
    {
      metric: "35%",
      description: "More leads captured off-hours",
      icon: <Rocket size={28} />
    },
    {
      metric: "28%",
      description: "Increase in booking conversion rate",
      icon: <BarChart size={28} />
    }
  ];

  // Chat conversation examples
  const conversations = [
    {
      customer: "Hi, I'm interested in getting my car detailed. Do you offer ceramic coating?",
      chip: "Hi there! Yes, we offer several ceramic coating packages with different durability options and price points. Are you looking for a coating for a daily driver or a show car? Also, what type of vehicle do you have?"
    },
    {
      customer: "It's for my BMW X5, daily driver but I want good protection.",
      chip: "Great choice! For a daily driven BMW X5, I'd recommend our 5-year ceramic coating package which offers excellent protection against environmental contaminants, UV damage, and makes maintenance much easier. It's priced at $1,499 for your vehicle size. Would you like to see what's included or check availability for an appointment?"
    },
    {
      customer: "What dates do you have available next week?",
      chip: "Let me check our schedule for next week for the ceramic coating service on your BMW X5. We have openings on Tuesday (May 16) at 9am, Thursday (May 18) at 1pm, and Friday (May 19) at 10am. The service typically takes 2-3 days to complete. Which day works best for you?"
    }
  ];

  // Voice Mode features
  const voiceModeFeatures = [
    {
      title: "Natural Voice Conversations",
      description: "CHiP will respond to customer calls with a natural-sounding voice that's warm, professional, and indistinguishable from your best staff.",
      icon: <Volume2 className="text-accent" size={24} />
    },
    {
      title: "24/7 Phone Support",
      description: "Never miss another call - CHiP answers every time, qualifying leads, booking appointments, and handling inquiries around the clock.",
      icon: <Headphones className="text-accent" size={24} />
    },
    {
      title: "Seamless Call Routing",
      description: "Smart call handling to direct priority customers to your team while managing routine questions automatically.",
      icon: <Phone className="text-accent" size={24} />
    }
  ];

  return (
    <div className="pt-32 pb-20">
      {/* SEO Metadata */}
      <SiteMetadata 
        page="chip"
        title="CHiP AI Assistant - 24/7 Sales Assistant for Auto Detailers"
        description="CHiP is the auto detailing industry's only AI assistant specifically trained to book appointments, qualify leads, and answer detailed questions about automotive services."
        keywords={[
          "auto detailing AI assistant", 
          "detailing business automation", 
          "car detailing lead generation",
          "auto detailing chatbot",
          "detailing business conversion rates",
          "auto detailing appointment booking",
          "automotive service AI assistant"
        ]}
      />

      {/* Background gradient */}
      <motion.div 
        className="absolute rounded-full opacity-60 blur-3xl -z-10 w-[800px] h-[800px] top-[-300px] right-[10%]"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.6, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(204, 255, 0, 0.3), rgba(204, 255, 0, 0) 70%)'
        }}
      />
      
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <SectionTransitionEffect>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.span 
              className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Meet CHiP
            </motion.span>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6">Your 24/7 AI Sales Assistant</h1>
            <p className="text-xl text-white/80 mb-8">
              CHiP is the detailing industry's only AI assistant specifically trained to book appointments, 
              qualify leads, and answer detailed questions about automotive services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button 
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Add CHiP to Your Business
              </motion.button>
              <motion.button 
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Watch Demo
              </motion.button>
            </div>
          </div>
        </SectionTransitionEffect>
        
        {/* Speed to Lead Section */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <motion.div 
              className="glassmorphic rounded-xl p-8 border-accent/20 shadow-glow-sm overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">The 5-Minute Lead Response Window</h2>
                <p className="text-xl text-white/80 max-w-3xl mx-auto">
                  In the auto detailing industry, <span className="text-accent font-semibold">you have just 5 minutes</span> to 
                  respond to a new lead before your chances of conversion drop dramatically.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Timer className="text-accent" size={24} />
                    <span>Conversion Rate by Response Time</span>
                  </h3>
                  
                  <div className="space-y-4">
                    {conversionData.map((data, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{data.time}</span>
                          <span className={index <= 1 ? "text-accent font-semibold" : "text-white/80"}>
                            {data.rate}% Conversion
                          </span>
                        </div>
                        <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                          <motion.div 
                            className={`h-full ${data.color}`}
                            initial={{ width: 0 }}
                            whileInView={{ width: `${data.rate}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-3 bg-accent/10 border border-accent/20 rounded-lg">
                    <p className="text-sm flex items-start gap-2">
                      <AlertCircle size={18} className="text-accent flex-shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-accent">Critical finding:</strong> After just 5 minutes, 
                        your chance of converting a lead drops by over 20%, and continues to decline rapidly.
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <LineChart className="text-accent" size={24} />
                      <span>Why Rapid Response Matters</span>
                    </h3>
                    
                    <ul className="space-y-3">
                      {[
                        "Customers contact multiple detailers simultaneously",
                        "First to respond often wins the business",
                        "Immediate response shows professionalism",
                        "Modern customers expect instant gratification",
                        "After-hours inquiries are highest value leads"
                      ].map((point, index) => (
                        <motion.li 
                          key={index} 
                          className="flex items-start gap-2"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3 + (index * 0.1) }}
                        >
                          <CheckCircle size={18} className="text-accent mt-1 flex-shrink-0" />
                          <span>{point}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-6 p-4 bg-black/30 rounded-lg border border-white/10">
                    <p className="text-lg font-semibold mb-2">The Reality Check:</p>
                    <p className="text-white/80">
                      Most shops take <span className="text-red-400 font-semibold">5+ hours</span> to respond to leads,
                      and completely miss after-hours inquiries until the next business day—when it's too late.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center max-w-3xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                >
                  <h3 className="text-2xl font-bold mb-4">CHiP solves this problem.</h3>
                  <p className="text-lg text-white/80 mb-6">
                    CHiP responds to every inquiry within <span className="text-accent font-semibold">seconds</span>, 
                    24 hours a day, 7 days a week—capturing leads while your competition is sleeping.
                  </p>
                  
                  <motion.button 
                    className="btn btn-primary inline-flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Never Miss a Lead Again</span>
                    <ChevronRight size={20} />
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
          </SectionTransitionEffect>
        </div>
        
        {/* Business Pain Points */}
        <div className="mb-24">
          <SectionTransitionEffect>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Common Challenges
              </motion.span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-6">Detailing Business Pain Points</h2>
              <p className="text-white/70 text-lg">
                Mid-market detailing businesses face specific challenges that impact growth and profitability.
                CHiP is designed to address these exact pain points.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                className="glassmorphic rounded-xl p-6 hover:border-accent/20 transition-colors"
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  {point.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{point.title}</h3>
                <p className="text-white/70">{point.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
        
        {/* Live Chat Demo */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect delay={0.2}>
            <motion.div 
              className="glassmorphic rounded-xl border border-white/10 overflow-hidden shadow-glow-sm"
              animate={{ 
                boxShadow: ["0 0 15px rgba(204, 255, 0, 0.2)", "0 0 25px rgba(204, 255, 0, 0.4)", "0 0 15px rgba(204, 255, 0, 0.2)"]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* Chat Header */}
              <div className="bg-black/30 p-4 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Bot size={32} className="text-accent" />
                    <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></span>
                  </div>
                  <div>
                    <div className="font-bold">CHiP</div>
                    <div className="text-xs text-white/60 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      Online 24/7
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <motion.div 
                    className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Powered by AI
                  </motion.div>
                </div>
              </div>
              
              {/* Chat Messages */}
              <div className="p-6 space-y-6 max-h-[500px] overflow-y-auto bg-gradient-to-b from-black/30 to-transparent">
                {/* Welcome Message */}
                <motion.div 
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-background flex-shrink-0">
                    <Bot size={18} />
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 max-w-[80%]">
                    <p>
                      👋 Hi there! I'm CHiP, your personal detailing assistant. I can help you book appointments, 
                      learn about our services, or answer any questions about car detailing and protection. 
                      How can I help you today?
                    </p>
                  </div>
                </motion.div>
                
                {/* Conversation Examples */}
                {conversations.map((convo, index) => (
                  <React.Fragment key={index}>
                    <motion.div 
                      className="flex items-start gap-3 justify-end"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 + (index * 0.8) }}
                    >
                      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 max-w-[80%]">
                        <p>{convo.customer}</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Users size={18} className="text-white/80" />
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + (index * 0.8) }}
                    >
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-background flex-shrink-0">
                        <Bot size={18} />
                      </div>
                      <div className="bg-white/5 rounded-lg p-4 max-w-[80%]">
                        <p>{convo.chip}</p>
                      </div>
                    </motion.div>
                  </React.Fragment>
                ))}
                
                {/* Typing Indicator */}
                <motion.div 
                  className="flex items-start gap-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 4.2 }}
                >
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-background flex-shrink-0">
                    <Bot size={18} />
                  </div>
                  <div className="bg-white/5 rounded-lg px-4 py-3 max-w-[80%]">
                    <div className="flex space-x-2">
                      <motion.div 
                        className="w-2 h-2 bg-white/50 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.8, repeat: Infinity, repeatType: "loop" }}
                      />
                      <motion.div 
                        className="w-2 h-2 bg-white/50 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.8, repeat: Infinity, repeatType: "loop", delay: 0.2 }}
                      />
                      <motion.div 
                        className="w-2 h-2 bg-white/50 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.8, repeat: Infinity, repeatType: "loop", delay: 0.4 }}
                      />
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Chat Input */}
              <div className="p-4 border-t border-white/10 bg-black/20">
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 focus:outline-none focus:border-accent"
                    placeholder="Type your message here..."
                    disabled
                  />
                  <motion.button
                    className="bg-accent text-background rounded-lg p-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled
                  >
                    <ArrowRight size={20} />
                  </motion.button>
                </div>
                <div className="mt-2 text-center text-xs text-white/50">
                  This is a demonstration. CHiP is ready to be deployed on your website and messaging channels.
                </div>
              </div>
            </motion.div>
          </SectionTransitionEffect>
        </div>
        
        {/* Feature Grid */}
        <div className="mb-24">
          <SectionTransitionEffect>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Powerful Capabilities
              </motion.span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-6">Everything CHiP Can Do For You</h2>
              <p className="text-white/70 text-lg">
                CHiP works 24/7 to engage customers, answer questions, and book appointments - all while you focus on growing your business.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="glassmorphic rounded-xl p-6 hover:border-accent/20 transition-colors"
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-white/70">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
        
        {/* ROI Section */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="glassmorphic rounded-xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -z-10 inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-60"></div>
              
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">The CHiP Advantage</h2>
                <p className="text-lg text-white/80">
                  Businesses using CHiP see dramatic improvements in lead capture, customer satisfaction, and booking rates.
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                {roiMetrics.map((item, index) => (
                  <motion.div 
                    key={index}
                    className="bg-white/5 rounded-xl p-6 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent mx-auto mb-4">
                      {item.icon}
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-accent mb-1">{item.metric}</div>
                    <p className="text-sm text-white/70">{item.description}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="bg-black/20 p-6 rounded-xl border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="bg-accent/20 p-2 rounded-full text-accent flex-shrink-0 mt-1">
                    <CheckCircle size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Customer feedback</h3>
                    <p className="italic text-white/80 mb-3">
                      "We added CHiP to our website and text messages last month. It's already booked 28 appointments and collected deposits automatically. The best part? My team isn't spending hours responding to basic questions anymore."
                    </p>
                    <div className="flex items-center gap-2">
                      <img 
                        src="https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=60" 
                        alt="Customer" 
                        className="w-8 h-8 rounded-full object-cover" 
                      />
                      <div>
                        <div className="font-semibold text-sm">Michael Johnson</div>
                        <div className="text-xs text-white/60">Precision Auto Detailing</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* How It Works */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Simple Integration
              </motion.span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-6">How CHiP Works</h2>
              <p className="text-white/70 text-lg">
                Getting started with CHiP is easy. We handle the setup, training, and optimization.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12 relative">
            {/* Connector line */}
            <div className="absolute top-0 bottom-0 left-1/2 transform -translate-x-1/2 w-px bg-gradient-to-b from-accent/30 via-accent to-accent/30 hidden md:block"></div>
            
            {[
              {
                step: 1,
                title: "Quick Setup",
                description: "We integrate CHiP with your website, SMS, and social channels in less than a day.",
                icon: <Rocket className="text-accent" size={24} />
              },
              {
                step: 2,
                title: "Customize Your CHiP",
                description: "We train CHiP with your services, pricing, booking preferences, and brand voice.",
                icon: <Sparkles className="text-accent" size={24} />
              },
              {
                step: 3,
                title: "Go Live & Scale",
                description: "CHiP starts working immediately, learning and improving with every conversation.",
                icon: <Globe className="text-accent" size={24} />
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
              >
                {/* Step number - appears on left for odd, right for even in desktop */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 md:translate-x-0 md:left-auto md:right-0 md:odd:left-0 md:odd:right-auto w-10 h-10 rounded-full bg-accent flex items-center justify-center text-background font-bold z-10 md:translate-y-0">
                  {item.step}
                </div>
                
                {/* Content */}
                <div className="glassmorphic rounded-xl p-6 ml-0 md:ml-4 md:even:ml-0 md:even:mr-4 mt-5 hover:border-accent/20 transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-white/70">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Coming Soon: Voice Mode */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="glassmorphic rounded-xl p-8 border-accent/30 shadow-glow-sm relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-accent/20 rounded-full blur-3xl -z-10"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl -z-10"></div>
              
              <div className="flex justify-center mb-8">
                <div className="bg-accent/30 px-4 py-2 rounded-full flex items-center gap-2 text-accent animate-pulse">
                  <Mic size={20} />
                  <span className="font-bold">COMING SOON: VOICE MODE</span>
                </div>
              </div>
              
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">CHiP Voice is Coming in Q3 2025</h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto">
                  CHiP is evolving beyond text. Soon, CHiP will handle your incoming phone calls with a natural-sounding voice that books appointments and answers questions 24/7.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                {voiceModeFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-accent/20 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-white/70">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <div className="flex items-center gap-3 bg-white/10 p-3 rounded-lg">
                  <Mic className="text-accent" size={24} />
                  <p className="text-sm">Natural, human-like voice with detailing industry expertise</p>
                </div>
                <div className="flex items-center gap-3 bg-white/10 p-3 rounded-lg">
                  <CheckCircle className="text-accent" size={24} />
                  <p className="text-sm">Join our early access waitlist for exclusive beta pricing</p>
                </div>
              </div>
              
              <div className="text-center mt-10">
                <motion.button
                  className="btn btn-primary inline-flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Join Voice Mode Waitlist</span>
                  <ChevronRight size={18} />
                </motion.button>
              </div>
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* Pricing Section */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="glassmorphic rounded-xl p-8 border-accent/50 shadow-glow-sm">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Add CHiP to Your Existing Plan</h2>
                <p className="text-lg text-white/80">
                  CHiP is available as a powerful add-on to any DetailerOS or GRIT SUITE subscription.
                </p>
              </div>
              
              <div className="glassmorphic rounded-xl p-6 border-accent hover:border-accent transition-colors max-w-xl mx-auto">
                <div className="bg-accent/20 text-accent font-bold text-sm px-4 py-2 rounded-full inline-block mb-4 animate-pulse">
                  🚀 Launch Pricing - Save $150! Limited Time Offer
                </div>
                
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-2">
                      Add-on Service
                    </div>
                    <h3 className="text-2xl font-bold">CHiP AI Assistant</h3>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">$347</span>
                      <span className="text-white/70">/month</span>
                    </div>
                    <div className="text-sm text-white/60 line-through">
                      $497/month
                    </div>
                    <div className="text-xs text-yellow-400 font-semibold">
                      Save $150/month!
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-3">
                    {[
                      "Full 24/7 AI assistant coverage",
                      "Website + SMS + Social integration",
                      "Unlimited conversations",
                      "Regular updates & improvements"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle size={18} className="text-accent mt-1 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    {[
                      "Dedicated support",
                      "Simple 30-day contracts",
                      "Custom training for your business",
                      "Detailed analytics dashboard"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle size={18} className="text-accent mt-1 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row gap-4">
                  <motion.button 
                    className="w-full btn btn-primary flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span>Add to My Plan</span>
                    <ChevronRight size={18} />
                  </motion.button>
                  
                  <Link to="/#pricing" className="w-full">
                    <motion.button 
                      className="w-full btn btn-secondary flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <span>View Plans</span>
                      <ChevronRight size={18} />
                    </motion.button>
                  </Link>
                </div>
                
                <div className="mt-6 p-3 bg-black/20 rounded-lg border border-white/10 text-center text-sm">
                  <p>Need CHiP as a standalone service? <a href="#contact" className="text-accent hover:underline">Contact us</a> for enterprise pricing.</p>
                </div>
              </div>
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* FAQ Section */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-4">
              {[
                {
                  question: "Does CHiP need training with my specific business information?",
                  answer: "While CHiP comes pre-trained with industry knowledge, we customize it with your specific services, prices, and brand voice during setup. The more information you provide, the more effective CHiP will be from day one."
                },
                {
                  question: "Can CHiP integrate with my existing booking system?",
                  answer: "Yes, CHiP integrates with most popular booking systems and CRMs used by detailing businesses. This includes systems like GRIT SUITE, Mobile Tech RX, Jobber, and ServiceM8. We'll handle the integration during setup."
                },
                {
                  question: "What happens when CHiP can't answer a question?",
                  answer: "CHiP is designed to recognize when a question is beyond its capabilities. In these cases, it will politely let the customer know a team member will follow up and instantly notify your staff with the full conversation context."
                },
                {
                  question: "How quickly can CHiP be implemented on my website?",
                  answer: "Most businesses are up and running with CHiP within 24-48 hours. The implementation process includes adding CHiP to your website, connecting it to your messaging channels, and customizing responses to match your services."
                },
                {
                  question: "Can I see what conversations CHiP is having with customers?",
                  answer: "Absolutely. You'll have access to a dashboard where you can view all conversations in real-time, review conversation history, and see performance metrics like booking rates and popular service inquiries."
                }
              ].map((faq, index) => (
                <motion.div 
                  key={index}
                  className="glassmorphic rounded-xl p-6 hover:border-accent/20 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-white/70">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* CTA Section */}
        <div className="max-w-3xl mx-auto">
          <SectionTransitionEffect>
            <motion.div 
              className="glassmorphic rounded-xl p-8 md:p-12 border-accent/30 shadow-glow-sm text-center"
              animate={{ 
                boxShadow: ["0 0 15px rgba(204, 255, 0, 0.2)", "0 0 25px rgba(204, 255, 0, 0.4)", "0 0 15px rgba(204, 255, 0, 0.2)"]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="w-16 h-16 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-6">
                <Bot size={32} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to let CHiP handle your leads?</h2>
              <p className="text-xl text-white/80 mb-8">
                Add a team member that works 24/7, never calls in sick, and consistently converts leads into booked appointments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button 
                  className="btn btn-primary text-lg px-8 py-4"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get CHiP Now
                </motion.button>
                <motion.button 
                  className="btn btn-secondary text-lg px-8 py-4"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Schedule a Demo
                </motion.button>
              </div>
            </motion.div>
          </SectionTransitionEffect>
        </div>
      </div>
    </div>
  );
};

export default Chip;